import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  RECORD_VIEW_MODES,
  RECORD_VIEW_MODE_STORAGE_KEY,
  normalizeRecordViewMode,
  readRecordViewMode,
  resolveEffectiveRecordViewMode,
  writeRecordViewMode,
} from '../utils/recordViewMode';

const SAME_TAB_EVENT = 'terrio:record-view-mode';

/**
 * Preferencia «por registro / dos por fila / agrupado por lote» persistida en
 * localStorage. Registros y Listas leen la misma clave: cambiarla en un panel
 * se refleja en el otro (evento `storage` entre pestañas y evento propio en la
 * misma pestaña).
 * @returns {{ viewMode: string, effectiveViewMode: string, setViewMode: Function, canUseGrid: boolean, isGrouped: boolean }}
 */
export function useRecordViewMode() {
  const theme = useTheme();
  // Mismo corte que el share: por debajo de md el panel no da dos columnas legibles.
  const canUseGrid = useMediaQuery(theme.breakpoints.up('md'));
  const [viewMode, setViewModeState] = useState(readRecordViewMode);

  useEffect(() => {
    const sync = (event) => {
      if (event?.type === 'storage' && event.key !== RECORD_VIEW_MODE_STORAGE_KEY) return;
      setViewModeState(readRecordViewMode());
    };
    window.addEventListener('storage', sync);
    window.addEventListener(SAME_TAB_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(SAME_TAB_EVENT, sync);
    };
  }, []);

  const setViewMode = useCallback((mode) => {
    const next = normalizeRecordViewMode(mode);
    writeRecordViewMode(next);
    setViewModeState(next);
    window.dispatchEvent(new Event(SAME_TAB_EVENT));
  }, []);

  const effectiveViewMode = resolveEffectiveRecordViewMode(viewMode, canUseGrid);
  return {
    viewMode,
    effectiveViewMode,
    setViewMode,
    canUseGrid,
    isGrouped: effectiveViewMode === RECORD_VIEW_MODES.GROUPED,
  };
}
